import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, SERVICES } from "@/lib/api";

const Stars = ({ value, className = "h-4 w-4" }) => (
  <span className="flex gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${className} ${i <= value ? "fill-[#EDB89E] text-[#EDB89E]" : "text-zinc-600"}`} />
    ))}
  </span>
);

const ReviewForm = () => {
  const [form, setForm] = useState({ name: "", service: "", rating: 5, text: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reviews", form);
      toast.success("Got it! We'll take a look and post it shortly.");
      setForm({ name: "", service: "", rating: 5, text: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const field = "h-12 w-full rounded-none border-white/15 bg-[#111317] text-base text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-[var(--acid)]";

  return (
    <form onSubmit={submit} className="border border-white/10 bg-[#111317] p-7 sm:p-9" data-testid="review-form">
      <p className="eyebrow text-acid">( Leave a review )</p>
      <h3 className="mt-3 font-display text-3xl uppercase text-zinc-50">Did we earn it?</h3>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <input value={form.name} onChange={set("name")} placeholder="Your name" required maxLength={80} className={field} data-testid="review-input-name" />
        <select value={form.service} onChange={set("service")} className={`${field} appearance-none`} data-testid="review-select-service">
          <option value="">Service (optional)</option>
          {SERVICES.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
        </select>
      </div>
      <div className="mt-5 flex items-center gap-3">
        <span className="eyebrow">Rating</span>
        {[1, 2, 3, 4, 5].map((i) => (
          <button type="button" key={i} onClick={() => setForm({ ...form, rating: i })} data-testid={`review-star-${i}`} aria-label={`${i} stars`}>
            <Star className={`h-6 w-6 transition-colors ${i <= form.rating ? "fill-[#EDB89E] text-[#EDB89E]" : "text-zinc-600 hover:text-zinc-400"}`} />
          </button>
        ))}
      </div>
      <textarea value={form.text} onChange={set("text")} rows={4} required maxLength={600}
        placeholder="Tell it like it is…" className="mt-5 w-full border border-white/15 bg-[#111317] p-4 text-base text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-[var(--acid)]" data-testid="review-input-text" />
      <button type="submit" disabled={busy} data-testid="review-submit-button"
        className="mt-6 rounded-full bg-acid px-7 py-3.5 font-bold text-black transition-transform duration-300 hover:scale-[1.04] disabled:opacity-50">
        Post my review
      </button>
    </form>
  );
};

export const Reviews = () => {
  const [reviews, setReviews] = useState(null);
  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(Array.isArray(r.data) ? r.data : [])).catch(() => setReviews([]));
  }, []);

  return (
    <section id="reviews" className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 sm:py-32" data-testid="reviews-section">
      <div className="mb-14 flex items-end justify-between gap-6">
        <h2 className="font-display text-5xl uppercase leading-[0.9] text-zinc-50 sm:text-7xl">
          The <span className="font-serif-i normal-case text-acid">receipts.</span>
        </h2>
        <p className="eyebrow hidden sm:block">( Straight from the cleaned )</p>
      </div>
      <div className="grid auto-rows-[minmax(0,auto)] gap-4 md:grid-cols-3">
        {reviews === null && <p className="text-zinc-400">Loading reviews…</p>}
        {reviews?.length === 0 && <p className="text-zinc-400">Fresh out of posted reviews. Be the first below.</p>}
        {(reviews || []).map((r, i) => (
          <motion.figure key={r.id || i} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col justify-between gap-6 border border-white/10 bg-[#111317] p-7 transition-colors duration-500 hover:border-white/25 sm:p-9" data-testid={`review-card-${i}`}>
            <div>
              <div className="flex items-center justify-between">
                <Stars value={r.rating} />
                {r.sample && <span className="rounded-full border border-white/15 px-2.5 py-1 font-mono-x text-[10px] uppercase tracking-widest text-zinc-500" data-testid={`review-sample-tag-${i}`}>sample</span>}
              </div>
              <blockquote className="mt-5 text-lg leading-relaxed text-zinc-200">&ldquo;{r.text}&rdquo;</blockquote>
            </div>
            <figcaption className="border-t border-white/10 pt-4">
              <span className="block font-semibold text-zinc-50">{r.name}</span>
              <span className="block text-sm text-zinc-400">{r.service}</span>
            </figcaption>
          </motion.figure>
        ))}
      </div>
      <div className="mt-14 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="eyebrow mb-5">( Your turn )</p>
          <h3 className="font-display text-4xl uppercase leading-[0.95] text-zinc-50 sm:text-5xl">Happy with the clean? <span className="font-serif-i normal-case text-acid">Say it loud.</span></h3>
          <p className="mt-5 max-w-md text-zinc-400">Reviews get posted after a quick once-over from us — the same way we check your baseboards.</p>
        </div>
        <div className="lg:col-span-7"><ReviewForm /></div>
      </div>
    </section>
  );
};
